<!-- La feuille prend la hauteur de son contenu (vw-toast-popup). -->
<!-- Abonné : le bandeau vert profond fait office d'en-tête. Sinon, la ligne
     de titre commune des feuilles. -->
<vw-toast-popup
  [isOpen]="isOpen"
  [title]="state?.isPremium ? '' : ('premium.manage.title' | translate)"
  (close)="onClose()"
>
  <div class="flex flex-col">
    @if (state?.isPremium) {
      <!-- L'état, en un bloc : de quoi il s'agit, ce qui reste, ce qui suit.
           Le vert profond est celui de la carte du profil et du paywall — un
           abonnement ne change pas d'identité selon l'écran. -->
      <div
        class="bg-gradient-to-br from-vw-premium-deep-from to-vw-premium-deep-to px-5 pb-5 pt-6 text-vw-on-color"
      >
        <!-- La place de la croix est réservée aux deux premières lignes seulement :
             la barre de progression prend toute la largeur. -->
        <p class="pr-12 text-body-s text-white/75">
          {{ "premium.manage.title" | translate }}
        </p>

        <div class="mt-2 flex items-center gap-2.5 pr-12">
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-gradient-to-br from-vw-premium-accent to-vw-premium-line"
          >
            <svg
              viewBox="0 0 24 24"
              class="h-5 w-5 text-vw-on-color"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z"
              />
            </svg>
          </span>
          <p
            class="text-caption font-semibold uppercase tracking-wide text-vw-premium-line"
          >
            @if (isGifted) {
              {{
                (state?.provider === "TRIAL"
                  ? "premium.manage.kind-trial"
                  : "premium.manage.kind-gift"
                ) | translate
              }}
            } @else {
              {{ "premium.profile.member-badge" | translate }}
            }
          </p>
        </div>

        <!-- Le chiffre qui compte d'abord : les jours restants quand l'accès a
             une fin, le prix quand il se renouvelle. -->
        <p class="mt-3 font-display text-title-l">
          @if (isGifted && daysLeft !== null) {
            {{ "premium.manage.days-left" | vwPlural: daysLeft }}
          } @else if (isCanceled && periodEndLabel) {
            {{ "premium.manage.ends-on" | translate: { date: periodEndLabel } }}
          } @else if (isGifted) {
            <!-- Un accès offert sans échéance connue : il n'y a ni compte à
                 rebours à afficher, ni prix qui aurait un sens ici. -->
            {{ "premium.profile.member-title" | translate }}
          } @else {
            {{
              (state?.plan === "YEARLY"
                ? "premium.paywall.yearly"
                : "premium.paywall.monthly"
              ) | translate: { price: pricing.label(state?.plan ?? "") }
            }}
          }
        </p>

        <p class="mt-0.5 text-body-s text-white/80">
          @if (isCanceled) {
            {{ "premium.manage.no-more-charge" | translate }}
          } @else if (isGifted && periodEndLabel) {
            {{ "premium.manage.until" | translate: { date: periodEndLabel } }}
          } @else if (periodEndLabel) {
            {{
              "premium.manage.next-renewal"
                | translate: { date: periodEndLabel }
            }}
          }
        </p>

        <!-- La barre dit d'un coup d'œil où l'on en est dans la période. Elle
             n'apparaît que si la durée est connue (un accès offert peut durer
             n'importe combien de temps) et que l'accès a une fin (essai,
             résilié) : pour un abonnement qui se renouvelle, elle ne mesure rien. -->
        @if (periodProgress !== null && showAfterSection) {
          <div
            class="mt-3 h-1.5 w-full overflow-hidden rounded-pill bg-white/20"
          >
            <div
              class="h-full rounded-pill bg-gradient-to-r from-vw-premium-line to-vw-premium-accent"
              [style.width.%]="periodProgress"
            ></div>
          </div>
        }

        @if (sinceLabel) {
          <p class="mt-2.5 text-caption font-normal text-white/75">
            {{
              "premium.manage.member-since" | translate: { date: sinceLabel }
            }}
          </p>
        }

        @if (state?.status === "PAST_DUE") {
          <!-- Une phrase, pas un titre qui la répète (P4.7.6) ; l'action est le
               bouton principal plus bas. -->
          <p class="mt-3 rounded-field bg-vw-warn-bg px-3 py-2 text-body-s text-vw-warn-ink" data-testid="past-due">
            {{ "premium.manage.past-due" | translate }}
          </p>
        }
      </div>
    }

    <div class="px-4" [class.pt-4]="state?.isPremium">
      @if (confirmingCancel) {
        <!-- Le message dit exactement jusqu'a quand l'acces reste ouvert -->
        <div class="rounded-card border border-vw-line px-4 py-4">
          <p class="pb-1 text-body font-semibold text-vw-ink">
            {{ "premium.manage.cancel-confirm-title" | translate }}
          </p>
          <p class="pb-4 text-body-s text-vw-muted">{{ cancelConfirmMessage }}</p>
          <!-- Motif facultatif (CR-09) : une question, aucune obligation. Le
               bouton de résiliation ne dépend jamais de la réponse. -->
          <p class="pb-2 text-body-s font-semibold text-vw-ink" aria-hidden="true">
            {{ "premium.manage.reason.title" | translate }}
          </p>
          <vw-choice-selector class="block pb-4" variant="list" [deselectable]="true" [options]="cancelReasons"
            [(value)]="cancelReason" [ariaLabel]="'premium.manage.reason.title' | translate" />
          <div class="flex gap-3">
            <vw-button
              look="clear"
              [label]="'premium.manage.cancel-yes' | translate"
              [loading]="loading"
              customClass="flex-1"
              (click)="confirmCancel()"
            />
            <vw-button
              look="primary"
              [label]="'premium.manage.cancel-no' | translate"
              customClass="flex-1"
              (click)="confirmingCancel = false"
            />
          </div>
        </div>
      } @else {
        <!-- Ce que l'échéance retire, concrètement. Un essai qui s'arrête sans
             qu'on ait jamais dit ce qu'il contenait ne se renouvelle pas : la
             liste donne les chiffres du gratuit, là où l'offre vante l'illimité. -->
        @if (showAfterSection) {
          <p
            class="text-caption font-semibold uppercase tracking-wide text-vw-muted"
          >
            {{
              "premium.manage.after.title" | translate: { date: periodEndLabel }
            }}
          </p>
          <ul class="mt-2 flex flex-col gap-2">
            <li class="flex items-start gap-2.5">
              <span
                class="mt-2 h-1.5 w-1.5 shrink-0 rounded-pill bg-vw-premium-line"
              ></span>
              <span class="text-body-s text-vw-ink">
                {{
                  "premium.manage.after.willy"
                    | translate: { count: willyFreeLimit }
                }}
              </span>
            </li>
            <li class="flex items-start gap-2.5">
              <span
                class="mt-2 h-1.5 w-1.5 shrink-0 rounded-pill bg-vw-premium-line"
              ></span>
              <span class="text-body-s text-vw-ink">
                {{ "premium.manage.after.lunar" | translate }}
              </span>
            </li>
            <li class="flex items-start gap-2.5">
              <span
                class="mt-2 h-1.5 w-1.5 shrink-0 rounded-pill bg-vw-premium-line"
              ></span>
              <span class="text-body-s text-vw-ink">
                {{ "premium.manage.after.harvest" | translate }}
              </span>
            </li>
          </ul>
        }

        @if (isCanceled) {
          <vw-button
            look="primary"
            [label]="'premium.manage.reactivate' | translate"
            [loading]="loading"
            customClass="w-full mt-4"
            (click)="reactivate()"
          />
        }

        @if (isGifted) {
          <!-- Essai ou mois offerts : rien à annuler, l'échéance fait foi. Le
               libellé ne dit PAS « Passer Premium » : la personne l'est déjà.
               Ce qu'on lui propose, c'est de ne pas perdre l'accès. -->
          <vw-button
            look="primary"
            [label]="'premium.manage.keep-access' | translate"
            customClass="w-full mt-4"
            (click)="onClose({ upgrade: true })"
          />
          <p class="mt-1.5 text-center text-caption font-normal text-vw-muted">
            {{
              "premium.manage.keep-access-hint"
                | translate: { date: periodEndLabel }
            }}
          </p>
        }

        <!-- Un mensuel n'avait aucun moyen de passer à l'annuel depuis cet
             écran : la seule porte était le paywall, que l'on masque justement
             aux abonnés. -->
        @if (canSwitchToYearly) {
          <button
            type="button"
            (click)="onClose({ upgrade: true })"
            class="mt-4 flex w-full items-center justify-between rounded-card border border-vw-surface-tint bg-vw-bg px-4 py-3 text-left"
          >
            <span class="min-w-0 pr-2">
              <span class="block text-body-s font-medium text-vw-ink">
                {{ "premium.manage.switch-yearly" | translate }}
              </span>
              <span class="block text-caption font-normal text-vw-muted">
                {{ "premium.paywall.yearly" | translate: { price: pricing.label("YEARLY") } }}
                @if (pricing.monthsFree(); as months) {
                  · {{ "premium.paywall.yearly-badge" | translate: { count: months } }}
                }
              </span>
            </span>
            <vw-chevron-right-icon class="block h-5 w-5 shrink-0 text-vw-primary" aria-hidden="true" />
          </button>
        }

        <!-- Abonnement du magasin : ni annulation ni facturation ici. La seule
             porte est celle du magasin, et « Restaurer » est exige par Apple. -->
        @if (isStore) {
          <p
            class="mt-4 rounded-card bg-vw-bg px-4 py-3 text-body-s text-vw-primary-ink"
          >
            {{ "premium.store.managed" | translate }}
          </p>
          <vw-button
            look="primary"
            [label]="manageInStoreKey | translate"
            customClass="w-full mt-3"
            (click)="openBilling()"
          />
          <button
            type="button"
            class="mt-3 min-h-target w-full text-center text-body-s font-medium text-vw-primary-ink underline disabled:opacity-50"
            [disabled]="restoring"
            (click)="restorePurchases()"
          >
            {{ "premium.store.restore" | translate }}
          </button>
          @if (restored === true) {
            <p class="mt-1 text-center text-caption font-normal text-vw-primary-ink">
              {{ "premium.store.restore-done" | translate }}
            </p>
          } @else if (restored === false) {
            <p class="mt-1 text-center text-caption font-normal text-vw-muted">
              {{ "premium.store.restore-none" | translate }}
            </p>
          }
        }

        @if (isStripe && state?.status === "PAST_DUE") {
          <vw-button
            look="primary"
            data-testid="update-payment"
            [label]="'premium.manage.update-payment' | translate"
            customClass="w-full mt-3"
            (click)="openBilling()"
          />
        } @else if (isStripe) {
          <button
            type="button"
            (click)="openBilling()"
            class="mt-3 flex min-h-target w-full items-center justify-between rounded-card border border-vw-line px-4 py-3 text-left"
          >
            <span class="text-body-s text-vw-ink">{{
              "premium.manage.billing" | translate
            }}</span>
            <svg
              class="h-4 w-4 text-vw-icon-idle"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        }
      }

      @if (error) {
        <p role="alert" class="mt-3 text-center text-body-s text-vw-danger">
          {{ "common.error" | translate }}
        </p>
      }
    </div>

    <!-- La sortie, en bas et séparée : accessible en deux gestes, jamais au
         milieu du contenu où elle passait pour l'action principale. -->
    <!-- `!isStore` : un abonnement du magasin ne se resilie pas ici, et offrir
         le bouton reviendrait a promettre ce que le back refuse. -->
    @if (!confirmingCancel && state?.isPremium && !isCanceled && !isStore) {
      <div class="mt-auto border-t border-vw-line px-4 pb-8 pt-3">
        <button
          type="button"
          (click)="confirmingCancel = true"
          class="min-h-target w-full text-center text-body-s text-vw-muted"
        >
          {{ cancelLabel | translate }}
        </button>
      </div>
    }
  </div>
</vw-toast-popup>
